import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { useRouter } from "next/router"

import { useAuthRedirectGate } from "@/app-shell/layouts/authRedirectGate"
import DefaultLayout from "@/app-shell/layouts/Default"
import { Show, useOrganization, useOrganizationList } from "@clerk/nextjs"

import { logger } from "@/lib/logger"
import { RestrictedAccessGuard } from "@/components/access-control/RestrictedAccessGuard"
import { NotLoggedIn } from "@/components/shared/error-messages/NotLoggedIn"
import {
  buildOrganizationHomePath,
  parseWorkspaceRoute,
} from "@/utils/workspaceRoutes"

const log = logger("LogInRequired")

const SELECT_ORG_PATH = "/select-org"
const SIGNUP_WELCOME_PATH = "/signup-welcome"

interface LogInRequiredProps {
  children: ReactNode
  preserveRouteOnOrgRecovery?: boolean
}

export const LogInRequired: React.FC<LogInRequiredProps> = (props) => {
  return <ClerkLogInRequired {...props} />
}

const ClerkLogInRequired: React.FC<LogInRequiredProps> = ({
  children,
  preserveRouteOnOrgRecovery = false,
}) => {
  const { organization, isLoaded: orgLoaded } = useOrganization()
  const {
    isLoaded: listLoaded,
    userMemberships,
    userInvitations,
    setActive,
  } = useOrganizationList({
    userMemberships: { infinite: true },
    userInvitations: { infinite: true },
  })
  const router = useRouter()

  const redirectingToSignup = useAuthRedirectGate()

  const memberships = userMemberships?.data
  const invitations = userInvitations?.data
  const membershipsLoaded = listLoaded && memberships !== undefined
  const invitationsLoaded = listLoaded && invitations !== undefined
  const hasInvitations = (invitations?.length ?? 0) > 0
  // Recover a session without an active organization to its first membership.
  const firstMembershipSlug = memberships?.[0]?.organization.slug
  const shouldRecoverToFirstOrg =
    orgLoaded &&
    membershipsLoaded &&
    !organization &&
    Boolean(firstMembershipSlug)

  // No active org and nothing to recover, but a pending invitation exists → send
  // them to /select-org to accept it (the only membership-less reason to stay).
  const shouldRedirectToSelectOrg =
    orgLoaded &&
    membershipsLoaded &&
    invitationsLoaded &&
    !organization &&
    !firstMembershipSlug &&
    hasInvitations &&
    router.pathname !== SELECT_ORG_PATH

  // No org, no membership, no invite → the only way to get an org is self-serve
  // signup (ad-hoc Clerk org creation has been removed). Funnel to the controlled
  // signup flow rather than leaving them on a dead-end screen.
  const shouldRedirectToSignupWelcome =
    orgLoaded &&
    membershipsLoaded &&
    invitationsLoaded &&
    !organization &&
    !firstMembershipSlug &&
    !hasInvitations &&
    router.pathname !== SIGNUP_WELCOME_PATH

  // One latch holding the redirect currently in flight, so two destinations
  // never compete. A failed or canceled `router.replace` (rejection, or a
  // `false` resolution) clears it so the next render's conditions get a real
  // attempt instead of a permanently blank page. The clear is conditional on
  // this attempt still being the pending one, so a stale attempt settling after
  // a newer destination never unlatches that newer navigation.
  const pendingRedirectHrefRef = useRef<string | null>(null)
  const [recoveryFailed, setRecoveryFailed] = useState(false)
  const firstMembershipId = memberships?.[0]?.organization.id
  const redirectOnce = useCallback(
    (destination: string) => {
      if (pendingRedirectHrefRef.current === destination) {
        return
      }
      pendingRedirectHrefRef.current = destination
      router.replace(destination).then(
        (succeeded) => {
          if (!succeeded && pendingRedirectHrefRef.current === destination) {
            pendingRedirectHrefRef.current = null
          }
        },
        (error) => {
          if (pendingRedirectHrefRef.current === destination) {
            pendingRedirectHrefRef.current = null
          }
          log.error("Redirect failed", error)
        },
      )
    },
    [router],
  )

  useEffect(() => {
    if (pendingRedirectHrefRef.current !== null) {
      return
    }
    if (shouldRecoverToFirstOrg && firstMembershipSlug) {
      if (preserveRouteOnOrgRecovery && firstMembershipId && setActive) {
        setRecoveryFailed(false)
        pendingRedirectHrefRef.current = firstMembershipId
        void setActive({ organization: firstMembershipId }).then(
          () => {
            if (pendingRedirectHrefRef.current === firstMembershipId) {
              pendingRedirectHrefRef.current = null
            }
          },
          () => {
            if (pendingRedirectHrefRef.current === firstMembershipId) {
              pendingRedirectHrefRef.current = null
              setRecoveryFailed(true)
            }
          },
        )
      } else {
        redirectOnce(buildOrganizationHomePath(firstMembershipSlug))
      }
    } else if (shouldRedirectToSelectOrg) {
      redirectOnce(SELECT_ORG_PATH)
    } else if (shouldRedirectToSignupWelcome) {
      redirectOnce(SIGNUP_WELCOME_PATH)
    }
  }, [
    shouldRecoverToFirstOrg,
    firstMembershipSlug,
    firstMembershipId,
    preserveRouteOnOrgRecovery,
    setActive,
    shouldRedirectToSelectOrg,
    shouldRedirectToSignupWelcome,
    redirectOnce,
  ])

  if (recoveryFailed && !organization) {
    return (
      <p role="alert">
        Your organization could not be selected. Reload this page to try again.
      </p>
    )
  }

  if (!orgLoaded || !membershipsLoaded || !invitationsLoaded) {
    return null
  }

  if (
    shouldRecoverToFirstOrg ||
    shouldRedirectToSelectOrg ||
    shouldRedirectToSignupWelcome ||
    redirectingToSignup
  ) {
    return null
  }

  // Get the current page path for access control
  const currentPage = router.asPath.split("?")[0] // Remove query params
  const workspaceRoute = parseWorkspaceRoute(currentPage)
  const pagePath = workspaceRoute
    ? workspaceRoute.relativePath
    : currentPage.replace(`/${organization?.slug || ""}`, "") // Remove org slug (legacy)

  return (
    <DefaultLayout>
      <Show when="signed-in">
        {organization ? (
          <RestrictedAccessGuard requiredPage={pagePath}>
            {children}
          </RestrictedAccessGuard>
        ) : null}
      </Show>
      <Show when="signed-out">
        <NotLoggedIn />
      </Show>
    </DefaultLayout>
  )
}
